import React, { useEffect, useState } from 'react';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { ProjectCrystalDTO } from 'business/models/Models';
import CrystalInfoDetailPanel from './CrystalInfoDetailPanel';
import RMSelect from 'components/customComponents/Controls/RMSelect';
import { IStyle } from 'Styles';
import SwitchButton from 'components/Button/SwitchButton/switchButton';
import ESortOrder from 'enums/ESortOrder';
import './CrystalInfoPanel.scss';

export interface CrystalInfoPanelProps {
  crystalDTO: ProjectCrystalDTO[];
}

const optionsComboDetail = [
  {
    name: 'Experiment Name',
    id: 1,
    value: 'experimentName',
  },
  {
    name: 'Crystal Name',
    id: 2,
    value: 'name',
  },
  {
    name: 'Protein Name',
    id: 3,
    value: 'proteinName',
  },
  {
    name: 'Resolution',
    id: 4,
    value: 'resolution',
  },
  {
    name: 'Structure',
    id: 5,
    value: 'structure',
  },
  {
    name: 'Date Frozen',
    id: 6,
    value: 'dateFrozen',
  },
  {
    name: 'Data Collected',
    id: 7,
    value: 'dataCollected',
  },
  {
    name: 'Soaking',
    id: 8,
    value: 'soaking',
  },
];
const NEXT_SORT_ORDER: Record<ESortOrder, ESortOrder> = {
  [ESortOrder.Ascending]: ESortOrder.Descending,
  [ESortOrder.Descending]: ESortOrder.Ascending,
};
const SORT_ORDER_ICON_NAMES: Record<ESortOrder, 'OrderBy' | 'OrderByDesc'> = {
  [ESortOrder.Ascending]: 'OrderBy',
  [ESortOrder.Descending]: 'OrderByDesc',
};
const compareValues = (key?, order = ESortOrder.Ascending) => {
  return (firstObject, secondObject) => {
    if (!key || !firstObject.hasOwnProperty(key) || !secondObject.hasOwnProperty(key)) {
      // property doesn't exist on either object
      return 0;
    }

    const valueFirstObject =
      typeof firstObject[key] === 'string'
        ? firstObject[key].toUpperCase()
        : firstObject[key];
    const valueSecondObject =
      typeof secondObject[key] === 'string'
        ? secondObject[key].toUpperCase()
        : secondObject[key];

    let comparison = 0;
    if (valueFirstObject > valueSecondObject) {
      comparison = 1;
    } else if (valueFirstObject < valueSecondObject) {
      comparison = -1;
    }
    return order === 'desc' ? comparison * -1 : comparison;
  };
};

const CrystalInfoPanel = (props: CrystalInfoPanelProps) => {
  const { crystalDTO } = props;
  const [combo, setCombo] = useState(optionsComboDetail.slice(0, 3));
  const [selectedOption, setSelectedOptionState] = useState(optionsComboDetail[0].id);
  const [crystalState, setCrystalState] = useState(crystalDTO.sort(compareValues()));
  const [isShowCrystalDetail, setIsShowCrystalDetail] = useState(false);

  const [sortOrder, setSortOrder] = useState(ESortOrder.Ascending);

  useEffect(() => {
    const arr = [...crystalDTO];
    arr.sort(
      compareValues(
        optionsComboDetail.find((x) => x.id === +selectedOption).value,
        sortOrder,
      ),
    );
    setCrystalState(arr);
  }, [crystalDTO, selectedOption, sortOrder]);

  useEffect(() => {
    isShowCrystalDetail
      ? setCombo(optionsComboDetail)
      : setCombo(optionsComboDetail.slice(0, 3));
  }, [isShowCrystalDetail]);

  const onChangeOptions = (event) => {
    setSelectedOptionState(event.target.value);
  };

  const handleSortOrderToggle = () => {
    setSortOrder((currentOrder) => NEXT_SORT_ORDER[currentOrder]);
  };

  const getCrystalInfo = () => {
    const content = [];
    {
      crystalState.map((crystal) =>
        content.push(
          <div
            id={`crystalinfopanel-crystalinfo-${crystal.name}-wrapper`}
            className="crystal-info-card"
          >
            <div
              id={`crystalinfopanel-crystalinfo-${crystal.name}-mainrow`}
              className="row"
            >
              <div
                id={`crystalinfopanel-crystalinfo-${crystal.name}-iconcol`}
                className="col-1 crystal-info-icon-col"
              >
                <RMSvgIcon
                  id={`crystalinfopanel-crystalinfo-${crystal.name}-icon`}
                  name={crystal.dataCollected ? 'CrystalsGrey' : 'Crystals'}
                  fill="none"
                  width={40}
                  height={40}
                />
              </div>
              <div
                id={`crystalinfopanel-crystalinfo-${crystal.name}-textcol`}
                className="col-6 crystal-info-text-col"
              >
                <div id={`crystalinfopanel-crystalinfo-${crystal.name}-title`}>
                  {crystal.name}
                </div>
                <div
                  id={`crystalinfopanel-crystalinfo-${crystal.experimentName}-experiment`}
                  className="crystal-info-secondary-text"
                >
                  {crystal.experimentName}
                </div>
                <div
                  id={`crystalinfopanel-crystalinfo-${crystal.name}-protein`}
                  className="crystal-info-secondary-text"
                >
                  {crystal.proteinName}
                </div>
              </div>
            </div>

            <div
              id={`crystalinfopanel-crystalinfo-${crystal.name}-detailrow`}
              className="row crystal-info-detail-row"
              hidden={!isShowCrystalDetail}
            >
              <div
                id={`crystalinfopanel-crystalinfo-${crystal.name}-infocol`}
                className="col-1 crystal-info-detail-icon-col"
              >
                <RMSvgIcon
                  id={`crystalinfopanel-crystalinfo-${crystal.name}-infoicon`}
                  name="Info"
                  fill="#808080"
                  width={25}
                  height={25}
                />
              </div>
              <div
                id={`crystalinfopanel-crystalinfo-${crystal.name}-detailcol`}
                className="col-11 crystal-info-detail-col"
              >
                <CrystalInfoDetailPanel crystalDetail={crystal} />
              </div>
            </div>
          </div>,
        ),
      );
    }
    return content;
  };

  const getCrystalHeader = () => {
    return (
      <>
        <div
          id="crystalinfopanel-crystalheader-wrapper"
          className="nodeInfo nodeLabel row crystal-info-header-wrapper"
        >
          <div id="crystalinfopanel-crystalheader-title" className="col-6">
            Crystal Information
          </div>
          <div
            id="crystalinfopanel-crystalheader-count"
            className="col-5 crystal-info-count"
          >
            {crystalState.length}
          </div>
        </div>
        <div
          id={`crystalinfopanel-crystalheader-${styles.crystalheader}-controls`}
          className="row crystal-info-header-controls"
        >
          <div id="crystalinfopanel-crystalheader-sort-label" className="nodeInfo">
            Sort By
          </div>
          <div
            id="crystalinfopanel-crystalheader-sort-controls"
            className="crystal-info-sort-controls"
          >
            <RMSelect
              options={combo}
              selectedValue={selectedOption}
              className="padding5"
              onChange={onChangeOptions}
              placeholder={'Sort By'}
              style={styles.combo}
            />
            <RMSvgIcon
              id="crystalinfopanel-crystalheader-sort-icon"
              name={SORT_ORDER_ICON_NAMES[sortOrder]}
              width={30}
              height={30}
              className="crystal-info-sort-icon"
              onClick={handleSortOrderToggle}
            />
          </div>

          <label id="crystalinfopanel-crystalheader-showdetails-label">
            Show Details
          </label>
          <SwitchButton
            customStyle={styles.switchStyle}
            isToggled={isShowCrystalDetail}
            onToggle={() => setIsShowCrystalDetail(!isShowCrystalDetail)}
          />
        </div>
      </>
    );
  };
  return (
    <div id={`crystalinfopanel-${styles.crystalheader}`} className="crystal-info-panel">
      {getCrystalHeader()}
      <div id={`crystalinfopanel-${styles.crystalinfo}`} className="crystal-info-list">
        {getCrystalInfo()}
      </div>
    </div>
  );
};

const styles: IStyle = {
  combo: {
    width: 'auto',
    height: 'auto',
    flexDirection: 'column',
  },
  switchStyle: {
    paddingInline: '1%',
    marginLeft: '1%',
  },
};
export default CrystalInfoPanel;
